import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ImageUpload } from "@/components/admin/ImageUpload";
import { LogOut, Save, Plus, Trash2, ExternalLink, Loader2 } from "lucide-react";

const Field = ({ label, value, onChange, textarea }) => (
  <div>
    <label className="text-xs uppercase tracking-widest text-neutral-400">{label}</label>
    {textarea ? (
      <textarea
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        rows={3}
        className="mt-2 w-full rounded-xl bg-[#0A0A0A] border border-white/10 px-4 py-3 focus:border-[#FF5A00] focus:outline-none transition resize-none"
      />
    ) : (
      <input
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="mt-2 w-full rounded-xl bg-[#0A0A0A] border border-white/10 px-4 py-3 focus:border-[#FF5A00] focus:outline-none transition"
      />
    )}
  </div>
);

const TABS = [
  { id: "info", label: "Información" },
  { id: "menu", label: "Menú" },
  { id: "gallery", label: "Galería" },
  { id: "reviews", label: "Reseñas" },
  { id: "messages", label: "Mensajes" },
];

export default function AdminPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("info");
  const [content, setContent] = useState(null);
  const [menu, setMenu] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [messages, setMessages] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user === false) navigate("/login");
  }, [user, navigate]);

  useEffect(() => {
    if (!user) return;
    Promise.all([api.get("/content"), api.get("/menu"), api.get("/gallery"), api.get("/reviews"), api.get("/contact")]).then(
      ([c, m, g, r, msg]) => {
        setContent(c.data);
        setMenu(m.data);
        setGallery(g.data);
        setReviews(r.data);
        setMessages(msg.data);
      }
    );
  }, [user]);

  if (!user || !content) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A]">
        <Loader2 className="animate-spin text-[#FF5A00]" />
      </div>
    );
  }

  const setC = (k, v) => setContent((p) => ({ ...p, [k]: v }));

  const saveContent = async () => {
    setSaving(true);
    try {
      await api.put("/content", content);
      toast.success("Cambios guardados");
    } catch {
      toast.error("Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  // ---- Menu ops ----
  const addMenuItem = async () => {
    const { data } = await api.post("/menu", {
      category: "tacos", name_es: "Nuevo plato", name_en: "New dish",
      desc_es: "", desc_en: "", price: "$0.00", image: "", featured: false,
      order: menu.length,
    });
    setMenu([...menu, data]);
  };
  const updateMenuItem = (id, patch) => setMenu(menu.map((m) => (m.id === id ? { ...m, ...patch } : m)));
  const saveMenuItem = async (item) => {
    await api.put(`/menu/${item.id}`, item);
    toast.success("Plato guardado");
  };
  const delMenuItem = async (id) => {
    await api.delete(`/menu/${id}`);
    setMenu(menu.filter((m) => m.id !== id));
    toast.success("Plato eliminado");
  };

  // ---- Gallery ops ----
  const addGallery = async () => {
    const { data } = await api.post("/gallery", { image: "", caption_es: "", caption_en: "", order: gallery.length });
    setGallery([...gallery, data]);
  };
  const delGallery = async (id) => {
    await api.delete(`/gallery/${id}`);
    setGallery(gallery.filter((g) => g.id !== id));
    toast.success("Foto eliminada");
  };
  const updateGalleryLocal = (id, patch) => setGallery(gallery.map((g) => (g.id === id ? { ...g, ...patch } : g)));
  const saveGalleryItem = async (item) => {
    await api.delete(`/gallery/${item.id}`);
    const { data } = await api.post("/gallery", item);
    setGallery(gallery.map((g) => (g.id === item.id ? data : g)));
    toast.success("Foto guardada");
  };

  // ---- Reviews ops ----
  const addReview = async () => {
    const { data } = await api.post("/reviews", {
      author: "Cliente", rating: 5, text_es: "", text_en: "", date: "Hace poco", order: reviews.length,
    });
    setReviews([...reviews, data]);
  };
  const updateReviewLocal = (id, patch) => setReviews(reviews.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  const saveReview = async (item) => {
    await api.put(`/reviews/${item.id}`, item);
    toast.success("Reseña guardada");
  };
  const delReview = async (id) => {
    await api.delete(`/reviews/${id}`);
    setReviews(reviews.filter((r) => r.id !== id));
    toast.success("Reseña eliminada");
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-[#0A0A0A]/80 border-b border-white/10">
        <div className="max-w-6xl mx-auto px-5 md:px-8 h-16 flex items-center justify-between">
          <span className="font-display uppercase text-xl">El Rey del Sabor · CMS</span>
          <div className="flex items-center gap-3">
            <a href="/" target="_blank" rel="noreferrer" className="text-sm text-neutral-400 hover:text-white inline-flex items-center gap-1">
              <ExternalLink size={15} /> Ver sitio
            </a>
            <button data-testid="logout-btn" onClick={logout} className="inline-flex items-center gap-2 rounded-full border border-white/15 hover:border-[#FF5A00] px-4 py-2 text-sm font-semibold transition">
              <LogOut size={15} /> Salir
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-5 md:px-8 py-8">
        <div className="flex flex-wrap gap-2 mb-8">
          {TABS.map((tb) => (
            <button
              key={tb.id}
              data-testid={`tab-${tb.id}`}
              onClick={() => setTab(tb.id)}
              className={`rounded-full px-5 py-2.5 text-sm font-bold uppercase tracking-wide transition ${
                tab === tb.id ? "bg-[#FF5A00] text-black" : "border border-white/15 text-neutral-300 hover:border-white/50"
              }`}
            >
              {tb.label}
            </button>
          ))}
        </div>

        {/* INFO */}
        {tab === "info" && (
          <div className="space-y-6" data-testid="admin-info">
            <div className="rounded-2xl border border-white/10 bg-[#121212] p-6 grid md:grid-cols-2 gap-5">
              <Field label="Nombre del negocio" value={content.business_name} onChange={(v) => setC("business_name", v)} />
              <Field label="Calificación (ej. 5.0)" value={content.rating} onChange={(v) => setC("rating", v)} />
              <Field label="Número de reseñas en Google" value={content.review_count} onChange={(v) => setC("review_count", v)} />
              <Field label="Eslogan (ES)" value={content.tagline_es} onChange={(v) => setC("tagline_es", v)} />
              <Field label="Eslogan (EN)" value={content.tagline_en} onChange={(v) => setC("tagline_en", v)} />
              <Field label="Sobre nosotros (ES)" value={content.about_es} onChange={(v) => setC("about_es", v)} textarea />
              <Field label="Sobre nosotros (EN)" value={content.about_en} onChange={(v) => setC("about_en", v)} textarea />
            </div>
            <div className="rounded-2xl border border-white/10 bg-[#121212] p-6 grid md:grid-cols-3 gap-6">
              <ImageUpload label="Logo" value={content.logo} onChange={(v) => setC("logo", v)} />
              <ImageUpload label="Imagen principal (hero)" value={content.hero_image} onChange={(v) => setC("hero_image", v)} />
              <ImageUpload label="Imagen historia" value={content.about_image} onChange={(v) => setC("about_image", v)} />
            </div>
            <div className="rounded-2xl border border-white/10 bg-[#121212] p-6 grid md:grid-cols-2 gap-5">
              <Field label="Teléfono" value={content.phone} onChange={(v) => setC("phone", v)} />
              <Field label="WhatsApp (solo números, con código país)" value={content.whatsapp} onChange={(v) => setC("whatsapp", v)} />
              <Field label="Email" value={content.email} onChange={(v) => setC("email", v)} />
              <Field label="Sitio web" value={content.website} onChange={(v) => setC("website", v)} />
              <Field label="Dirección" value={content.address} onChange={(v) => setC("address", v)} />
              <Field label="Mapa (URL embed de Google Maps)" value={content.map_embed} onChange={(v) => setC("map_embed", v)} />
              <Field label="Horario (ES)" value={content.hours_es} onChange={(v) => setC("hours_es", v)} />
              <Field label="Horario (EN)" value={content.hours_en} onChange={(v) => setC("hours_en", v)} />
            </div>
            <button data-testid="save-content" onClick={saveContent} disabled={saving}
              className="inline-flex items-center gap-2 rounded-full bg-[#FF5A00] hover:bg-[#FF7A33] text-black font-bold uppercase tracking-wide px-7 py-3.5 transition-all hover:scale-105 disabled:opacity-60">
              {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Guardar cambios
            </button>
          </div>
        )}

        {/* MENU */}
        {tab === "menu" && (
          <div className="space-y-5" data-testid="admin-menu">
            <button data-testid="add-menu-item" onClick={addMenuItem}
              className="inline-flex items-center gap-2 rounded-full bg-[#A8E010] text-black font-bold uppercase tracking-wide px-5 py-2.5 transition hover:scale-105">
              <Plus size={16} /> Agregar plato
            </button>
            {menu.map((item) => (
              <div key={item.id} data-testid={`admin-menu-${item.id}`} className="rounded-2xl border border-white/10 bg-[#121212] p-6 grid md:grid-cols-2 gap-5">
                <div className="md:col-span-2 flex flex-wrap items-center gap-4">
                  <select value={item.category} onChange={(e) => updateMenuItem(item.id, { category: e.target.value })}
                    className="rounded-lg bg-[#0A0A0A] border border-white/10 px-3 py-2 text-sm focus:outline-none">
                    <option value="burritos">Burritos</option>
                    <option value="tacos">Tacos</option>
                    <option value="platos">Platos Fuertes</option>
                    <option value="antojitos">Antojitos</option>
                  </select>
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={item.featured} onChange={(e) => updateMenuItem(item.id, { featured: e.target.checked })} />
                    Destacado
                  </label>
                  <input value={item.price} onChange={(e) => updateMenuItem(item.id, { price: e.target.value })}
                    placeholder="Precio" className="rounded-lg bg-[#0A0A0A] border border-white/10 px-3 py-2 text-sm w-28 focus:outline-none" />
                </div>
                <Field label="Nombre (ES)" value={item.name_es} onChange={(v) => updateMenuItem(item.id, { name_es: v })} />
                <Field label="Nombre (EN)" value={item.name_en} onChange={(v) => updateMenuItem(item.id, { name_en: v })} />
                <Field label="Descripción (ES)" value={item.desc_es} onChange={(v) => updateMenuItem(item.id, { desc_es: v })} textarea />
                <Field label="Descripción (EN)" value={item.desc_en} onChange={(v) => updateMenuItem(item.id, { desc_en: v })} textarea />
                <ImageUpload label="Foto del plato" value={item.image} onChange={(v) => updateMenuItem(item.id, { image: v })} className="md:col-span-2" />
                <div className="md:col-span-2 flex gap-3">
                  <button onClick={() => saveMenuItem(item)} data-testid={`save-menu-${item.id}`}
                    className="inline-flex items-center gap-2 rounded-full bg-[#FF5A00] hover:bg-[#FF7A33] text-black font-bold text-sm uppercase px-5 py-2.5 transition">
                    <Save size={15} /> Guardar
                  </button>
                  <button onClick={() => delMenuItem(item.id)} data-testid={`del-menu-${item.id}`}
                    className="inline-flex items-center gap-2 rounded-full border border-red-500/40 text-red-400 hover:bg-red-500/10 font-bold text-sm uppercase px-5 py-2.5 transition">
                    <Trash2 size={15} /> Eliminar
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* GALLERY */}
        {tab === "gallery" && (
          <div className="space-y-5" data-testid="admin-gallery">
            <button data-testid="add-gallery" onClick={addGallery}
              className="inline-flex items-center gap-2 rounded-full bg-[#A8E010] text-black font-bold uppercase tracking-wide px-5 py-2.5 transition hover:scale-105">
              <Plus size={16} /> Agregar foto
            </button>
            <div className="grid md:grid-cols-2 gap-5">
              {gallery.map((g) => (
                <div key={g.id} data-testid={`admin-gallery-${g.id}`} className="rounded-2xl border border-white/10 bg-[#121212] p-6 space-y-4">
                  <ImageUpload label="Foto" value={g.image} onChange={(v) => updateGalleryLocal(g.id, { image: v })} />
                  <Field label="Título (ES)" value={g.caption_es} onChange={(v) => updateGalleryLocal(g.id, { caption_es: v })} />
                  <Field label="Título (EN)" value={g.caption_en} onChange={(v) => updateGalleryLocal(g.id, { caption_en: v })} />
                  <div className="flex gap-3">
                    <button onClick={() => saveGalleryItem(g)} className="inline-flex items-center gap-2 rounded-full bg-[#FF5A00] hover:bg-[#FF7A33] text-black font-bold text-sm uppercase px-5 py-2.5 transition">
                      <Save size={15} /> Guardar
                    </button>
                    <button onClick={() => delGallery(g.id)} data-testid={`del-gallery-${g.id}`}
                      className="inline-flex items-center gap-2 rounded-full border border-red-500/40 text-red-400 hover:bg-red-500/10 font-bold text-sm uppercase px-5 py-2.5 transition">
                      <Trash2 size={15} /> Eliminar
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* REVIEWS */}
        {tab === "reviews" && (
          <div className="space-y-5" data-testid="admin-reviews">
            <p className="text-sm text-neutral-400">Copia aquí tus reseñas de Google (o de clientes) para mostrarlas en el sitio y en los resultados de búsqueda.</p>
            <button data-testid="add-review" onClick={addReview}
              className="inline-flex items-center gap-2 rounded-full bg-[#A8E010] text-black font-bold uppercase tracking-wide px-5 py-2.5 transition hover:scale-105">
              <Plus size={16} /> Agregar reseña
            </button>
            {reviews.map((r) => (
              <div key={r.id} data-testid={`admin-review-${r.id}`} className="rounded-2xl border border-white/10 bg-[#121212] p-6 grid md:grid-cols-2 gap-5">
                <Field label="Autor" value={r.author} onChange={(v) => updateReviewLocal(r.id, { author: v })} />
                <div className="flex gap-4">
                  <div className="flex-1">
                    <label className="text-xs uppercase tracking-widest text-neutral-400">Estrellas</label>
                    <select value={r.rating} onChange={(e) => updateReviewLocal(r.id, { rating: parseInt(e.target.value) })}
                      className="mt-2 w-full rounded-xl bg-[#0A0A0A] border border-white/10 px-4 py-3 focus:outline-none">
                      {[5, 4, 3, 2, 1].map((n) => <option key={n} value={n}>{n} ★</option>)}
                    </select>
                  </div>
                  <div className="flex-1">
                    <Field label="Fecha" value={r.date} onChange={(v) => updateReviewLocal(r.id, { date: v })} />
                  </div>
                </div>
                <Field label="Reseña (ES)" value={r.text_es} onChange={(v) => updateReviewLocal(r.id, { text_es: v })} textarea />
                <Field label="Reseña (EN)" value={r.text_en} onChange={(v) => updateReviewLocal(r.id, { text_en: v })} textarea />
                <div className="md:col-span-2 flex gap-3">
                  <button onClick={() => saveReview(r)} data-testid={`save-review-${r.id}`}
                    className="inline-flex items-center gap-2 rounded-full bg-[#FF5A00] hover:bg-[#FF7A33] text-black font-bold text-sm uppercase px-5 py-2.5 transition">
                    <Save size={15} /> Guardar
                  </button>
                  <button onClick={() => delReview(r.id)} data-testid={`del-review-${r.id}`}
                    className="inline-flex items-center gap-2 rounded-full border border-red-500/40 text-red-400 hover:bg-red-500/10 font-bold text-sm uppercase px-5 py-2.5 transition">
                    <Trash2 size={15} /> Eliminar
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* MESSAGES */}
        {tab === "messages" && (          <div className="space-y-4" data-testid="admin-messages">
            {messages.length === 0 && <p className="text-neutral-500">Aún no hay mensajes.</p>}
            {messages.map((m) => (
              <div key={m.id} className="rounded-2xl border border-white/10 bg-[#121212] p-6">
                <div className="flex items-center justify-between">
                  <span className="font-bold">{m.name}</span>
                  <span className="text-xs text-neutral-500">{new Date(m.created_at).toLocaleString()}</span>
                </div>
                <a href={`mailto:${m.email}`} className="text-sm text-[#A8E010]">{m.email}</a>
                <p className="mt-3 text-neutral-300">{m.message}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
